/**
 * Negotiation Automation Types for Secondary Market Fund Dashboard
 * Ed Dua requirement (March 9, 2026): Volume offer management with automated posturing detection
 */

// ============================================================================
// ENUMS
// ============================================================================

export const NegotiationStage = {
  INITIAL_OUTREACH: 'initial_outreach',
  FIRST_RESPONSE: 'first_response',
  PRICING_DISCUSSION: 'pricing_discussion',
  COUNTER_OFFER: 'counter_offer',
  DUE_DILIGENCE_REQUEST: 'due_diligence_request',
  FINAL_NEGOTIATION: 'final_negotiation',
  ACCEPTANCE: 'acceptance',
  GP_CONSENT_COORDINATION: 'gp_consent_coordination',
} as const;

export type NegotiationStageType = (typeof NegotiationStage)[keyof typeof NegotiationStage];

export const PosturingIndicator = {
  STALLING: 'stalling',
  RESISTANT: 'resistant',
  RECEPTIVE: 'receptive',
  AGGRESSIVE: 'aggressive',
  COOPERATIVE: 'cooperative',
  SILENT: 'silent',
} as const;

export type PosturingIndicatorType = (typeof PosturingIndicator)[keyof typeof PosturingIndicator];

export const EmailSequenceStatus = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  SENT: 'sent',
  DELIVERED: 'delivered',
  OPENED: 'opened',
  REPLIED: 'replied',
  BOUNCED: 'bounced',
} as const;

export type EmailSequenceStatusType = (typeof EmailSequenceStatus)[keyof typeof EmailSequenceStatus];

export const TemplateCategory = {
  INITIAL_OUTREACH: 'initial_outreach',
  FOLLOW_UP: 'follow_up',
  COUNTER_OFFER: 'counter_offer',
  ACCEPTANCE: 'acceptance',
  GP_INTRODUCTION: 'gp_introduction',
  DUE_DILIGENCE: 'due_diligence',
  CLOSING_COORDINATION: 'closing_coordination',
} as const;

export type TemplateCategoryType = (typeof TemplateCategory)[keyof typeof TemplateCategory];

// ============================================================================
// CORE TYPES
// ============================================================================

export interface EmailTemplate {
  id: string;
  name: string;
  category: TemplateCategoryType;
  subject: string;
  body: string;
  placeholders: string[]; // e.g., ['{{deal_name}}', '{{discount}}', '{{seller_name}}']
  suggested_delay_hours?: number; // Delay before sending (for sequences)
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface EmailPlaceholder {
  key: string;
  label: string;
  description: string;
  default_value?: string;
  required: boolean;
}

export interface NegotiationThread {
  id: string;
  deal_id: string;
  stage: NegotiationStageType;
  posturing_indicator: PosturingIndicatorType;
  last_contact_date: string | null;
  next_suggested_action: string | null;
  next_action_date: string | null;
  response_time_avg_hours: number | null; // Average response time from counterparty
  total_emails_sent: number;
  total_emails_received: number;
  sentiment_score: number | null; // -1 to 1 (negative to positive)
  created_at: string;
  updated_at: string;
}

export interface EmailSequence {
  id: string;
  negotiation_thread_id: string;
  template_id: string;
  status: EmailSequenceStatusType;
  scheduled_send_date: string | null;
  sent_date: string | null;
  opened_date: string | null;
  replied_date: string | null;
  subject: string;
  body: string;
  recipient_email: string;
  recipient_name: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface PosturingSignal {
  indicator: PosturingIndicatorType;
  confidence: number; // 0-100
  signals: string[]; // e.g., ["No response in 7 days", "Short responses", "Price objections"]
  recommended_action: string;
}

export interface NegotiationActivity {
  id: string;
  negotiation_thread_id: string;
  activity_type: 'email_sent' | 'email_received' | 'call' | 'meeting' | 'note' | 'stage_change';
  description: string;
  actor: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface MultiDealNegotiationSummary {
  deal_id: string;
  deal_name: string;
  seller_name: string;
  stage: NegotiationStageType;
  posturing_indicator: PosturingIndicatorType;
  last_contact_date: string | null;
  next_action_date: string | null;
  days_since_last_contact: number | null;
  response_rate: number; // Percentage (0-100)
  target_value: number;
  headline_discount: number | null;
}

// ============================================================================
// TEMPLATE LIBRARY
// ============================================================================

export interface TemplatePlaceholderMapping {
  deal_name: string;
  seller_name: string;
  seller_first_name: string;
  fund_name: string;
  gp_name: string;
  target_value: string;
  headline_discount: string;
  true_discount: string;
  estimated_close_date: string;
  your_name: string;
  your_title: string;
  your_company: string;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const negotiationStageLabels: Record<NegotiationStageType, string> = {
  [NegotiationStage.INITIAL_OUTREACH]: 'Initial Outreach',
  [NegotiationStage.FIRST_RESPONSE]: 'First Response',
  [NegotiationStage.PRICING_DISCUSSION]: 'Pricing Discussion',
  [NegotiationStage.COUNTER_OFFER]: 'Counter Offer',
  [NegotiationStage.DUE_DILIGENCE_REQUEST]: 'Due Diligence Request',
  [NegotiationStage.FINAL_NEGOTIATION]: 'Final Negotiation',
  [NegotiationStage.ACCEPTANCE]: 'Acceptance',
  [NegotiationStage.GP_CONSENT_COORDINATION]: 'GP Consent Coordination',
};

export const negotiationStageColors: Record<NegotiationStageType, string> = {
  [NegotiationStage.INITIAL_OUTREACH]: 'bg-purple-50 text-purple-700 border-purple-200',
  [NegotiationStage.FIRST_RESPONSE]: 'bg-blue-50 text-blue-700 border-blue-200',
  [NegotiationStage.PRICING_DISCUSSION]: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  [NegotiationStage.COUNTER_OFFER]: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  [NegotiationStage.DUE_DILIGENCE_REQUEST]: 'bg-orange-50 text-orange-700 border-orange-200',
  [NegotiationStage.FINAL_NEGOTIATION]: 'bg-amber-50 text-amber-700 border-amber-200',
  [NegotiationStage.ACCEPTANCE]: 'bg-green-50 text-green-700 border-green-200',
  [NegotiationStage.GP_CONSENT_COORDINATION]: 'bg-lime-50 text-lime-700 border-lime-200',
};

export const posturingIndicatorLabels: Record<PosturingIndicatorType, string> = {
  [PosturingIndicator.STALLING]: 'Stalling',
  [PosturingIndicator.RESISTANT]: 'Resistant',
  [PosturingIndicator.RECEPTIVE]: 'Receptive',
  [PosturingIndicator.AGGRESSIVE]: 'Aggressive',
  [PosturingIndicator.COOPERATIVE]: 'Cooperative',
  [PosturingIndicator.SILENT]: 'Silent / No Response',
};

export const posturingIndicatorColors: Record<PosturingIndicatorType, string> = {
  [PosturingIndicator.STALLING]: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  [PosturingIndicator.RESISTANT]: 'bg-red-100 text-red-800 border-red-300',
  [PosturingIndicator.RECEPTIVE]: 'bg-green-100 text-green-800 border-green-300',
  [PosturingIndicator.AGGRESSIVE]: 'bg-orange-100 text-orange-800 border-orange-300',
  [PosturingIndicator.COOPERATIVE]: 'bg-blue-100 text-blue-800 border-blue-300',
  [PosturingIndicator.SILENT]: 'bg-gray-100 text-gray-800 border-gray-300',
};

export const posturingIndicatorIcons: Record<PosturingIndicatorType, string> = {
  [PosturingIndicator.STALLING]: '⏸️',
  [PosturingIndicator.RESISTANT]: '🛑',
  [PosturingIndicator.RECEPTIVE]: '✅',
  [PosturingIndicator.AGGRESSIVE]: '⚡',
  [PosturingIndicator.COOPERATIVE]: '🤝',
  [PosturingIndicator.SILENT]: '🔇',
};

export const templateCategoryLabels: Record<TemplateCategoryType, string> = {
  [TemplateCategory.INITIAL_OUTREACH]: 'Initial Outreach',
  [TemplateCategory.FOLLOW_UP]: 'Follow-Up',
  [TemplateCategory.COUNTER_OFFER]: 'Counter Offer',
  [TemplateCategory.ACCEPTANCE]: 'Acceptance',
  [TemplateCategory.GP_INTRODUCTION]: 'GP Introduction',
  [TemplateCategory.DUE_DILIGENCE]: 'Due Diligence',
  [TemplateCategory.CLOSING_COORDINATION]: 'Closing Coordination',
};

export const emailSequenceStatusLabels: Record<EmailSequenceStatusType, string> = {
  [EmailSequenceStatus.DRAFT]: 'Draft',
  [EmailSequenceStatus.SCHEDULED]: 'Scheduled',
  [EmailSequenceStatus.SENT]: 'Sent',
  [EmailSequenceStatus.DELIVERED]: 'Delivered',
  [EmailSequenceStatus.OPENED]: 'Opened',
  [EmailSequenceStatus.REPLIED]: 'Replied',
  [EmailSequenceStatus.BOUNCED]: 'Bounced',
};

export const emailSequenceStatusColors: Record<EmailSequenceStatusType, string> = {
  [EmailSequenceStatus.DRAFT]: 'text-gray-500',
  [EmailSequenceStatus.SCHEDULED]: 'text-purple-500',
  [EmailSequenceStatus.SENT]: 'text-blue-500',
  [EmailSequenceStatus.DELIVERED]: 'text-cyan-500',
  [EmailSequenceStatus.OPENED]: 'text-green-500',
  [EmailSequenceStatus.REPLIED]: 'text-green-700',
  [EmailSequenceStatus.BOUNCED]: 'text-red-500',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getDaysSinceLastContact(lastContactDate: string | null): number | null {
  if (!lastContactDate) return null;
  const lastContact = new Date(lastContactDate);
  const today = new Date();
  const diffTime = today.getTime() - lastContact.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getResponseRate(sent: number, received: number): number {
  if (sent === 0) return 0;
  return Math.round((received / sent) * 100);
}

export function getSuggestedFollowUpDelay(posturingIndicator: PosturingIndicatorType): number {
  // Returns hours to wait before follow-up based on posturing
  switch (posturingIndicator) {
    case PosturingIndicator.RECEPTIVE:
    case PosturingIndicator.COOPERATIVE:
      return 48; // 2 days
    case PosturingIndicator.STALLING:
      return 96; // 4 days
    case PosturingIndicator.RESISTANT:
      return 120; // 5 days
    case PosturingIndicator.AGGRESSIVE:
      return 24; // 1 day
    case PosturingIndicator.SILENT:
      return 168; // 7 days
    default:
      return 72; // 3 days default
  }
}

export function detectPosturingFromActivity(
  daysSinceLastContact: number | null,
  responseRate: number,
  avgResponseTimeHours: number | null
): PosturingSignal {
  const signals: string[] = [];
  let indicator: PosturingIndicatorType = PosturingIndicator.COOPERATIVE;
  let confidence = 50;

  // Silent detection
  if (daysSinceLastContact !== null && daysSinceLastContact > 10) {
    signals.push(`No response in ${daysSinceLastContact} days`);
    indicator = PosturingIndicator.SILENT;
    confidence = 85;
    return {
      indicator,
      confidence,
      signals,
      recommended_action: 'Send a brief check-in email or consider moving to other opportunities',
    };
  }

  // Stalling detection
  if (avgResponseTimeHours !== null && avgResponseTimeHours > 72) {
    signals.push('Slow response times (avg >3 days)');
    indicator = PosturingIndicator.STALLING;
    confidence += 20;
  }

  if (responseRate < 50) {
    signals.push(`Low response rate (${responseRate}%)`);
    indicator = PosturingIndicator.STALLING;
    confidence += 15;
  }

  // Receptive detection
  if (responseRate >= 80 && avgResponseTimeHours !== null && avgResponseTimeHours < 24) {
    signals.push('Quick responses');
    signals.push(`High response rate (${responseRate}%)`);
    indicator = PosturingIndicator.RECEPTIVE;
    confidence = 90;
  }

  // Cooperative detection
  if (responseRate >= 70 && avgResponseTimeHours !== null && avgResponseTimeHours < 48) {
    signals.push('Consistent engagement');
    indicator = PosturingIndicator.COOPERATIVE;
    confidence = 75;
  }

  const recommendedActions: Record<PosturingIndicatorType, string> = {
    [PosturingIndicator.STALLING]: 'Propose specific next steps with deadlines',
    [PosturingIndicator.RESISTANT]: 'Address objections directly; consider adjusting terms',
    [PosturingIndicator.RECEPTIVE]: 'Move quickly to LOI and due diligence',
    [PosturingIndicator.AGGRESSIVE]: 'Set clear boundaries; focus on mutual value',
    [PosturingIndicator.COOPERATIVE]: 'Maintain momentum; schedule next meeting',
    [PosturingIndicator.SILENT]: 'Send brief check-in or move to other opportunities',
  };

  return {
    indicator,
    confidence: Math.min(confidence, 100),
    signals,
    recommended_action: recommendedActions[indicator],
  };
}
